import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ClipboardDocumentIcon, SparklesIcon } from '@heroicons/react/24/outline';
import ChannelTrialNotice from '../common/ChannelTrialNotice';
import {
  connectOwnInstagramApp,
  disconnectInstagram,
  getInstagram,
  updateInstagram,
} from '../../services/tenant.service';

const label = 'block text-sm font-medium text-gray-700';
const input = 'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono';

const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

/**
 * A workspace's own Instagram account, answered by its AI agent.
 *
 * Only the "own app" path exists: Instagram messaging permissions need App
 * Review, so a customer connects the account they already run in their own
 * Meta app and points that app's webhook at the URL we hand back.
 */
const InstagramAgentCard = ({ tenantId, isSuperadmin = false }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [callbackUse, setCallbackUse] = useState('');
  const [fields, setFields] = useState({
    ig_account_id: '', page_id: '', access_token: '', app_secret: '',
  });

  const load = useCallback(async () => {
    try {
      setState(await getInstagram(tenantId));
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const set = (name) => (event) => setFields((prev) => ({ ...prev, [name]: event.target.value }));
  const ready = fields.ig_account_id.trim() && fields.access_token.trim()
    && fields.app_secret.trim() && callbackUse === 'dedicated';

  const connect = async (event) => {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try {
      const next = await connectOwnInstagramApp(tenantId, {
        ig_account_id: fields.ig_account_id.trim(),
        page_id: fields.page_id.trim() || null,
        access_token: fields.access_token.trim(),
        app_secret: fields.app_secret.trim(),
        dedicated_app_confirmed: true,
      });
      setState(next);
      setFields({ ig_account_id: '', page_id: '', access_token: '', app_secret: '' });
      setCallbackUse('');
      setOpen(false);
      toast.success('Instagram connected. Add the callback URL in your Meta app to start receiving messages.');
    } catch (err) {
      toast.error(errorText(err, 'Could not connect that account. Check the ID and the token.'));
    } finally {
      setBusy(false);
    }
  };

  const change = async (body, message) => {
    setBusy(true);
    try {
      setState(await updateInstagram(tenantId, body));
      toast.success(message);
    } catch (err) {
      toast.error(errorText(err, 'That did not work. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      setState(await disconnectInstagram(tenantId));
      toast.success('Instagram disconnected. Your chats stay in the inbox.');
    } catch (err) {
      toast.error(errorText(err, 'Could not disconnect. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (value, what) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${what} copied.`);
    } catch {
      toast(`Copy the ${what.toLowerCase()} from the box above.`);
    }
  };

  if (loading || !state) return null;
  const { entitled, connection } = state;

  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm" aria-labelledby="ig-agent-title">
      <div className="border-b border-gray-200 px-5 py-4">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-pink-600" aria-hidden="true" />
          <h2 id="ig-agent-title" className="text-lg font-semibold text-gray-900">AI agent on your Instagram</h2>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Your AI agent answers direct messages on your own Instagram account. Reply from the
          Instagram app and the AI stands down for 12 hours, while every chat stays in Serviti.
        </p>
      </div>

      <div className="space-y-4 px-5 py-4">
        <ChannelTrialNotice until={state.trial_until} tenantId={tenantId} />
        {!entitled && (
          <p className="text-sm text-gray-700">
            {connection
              ? 'The AI agent on Instagram add-on isn’t active on this workspace, so the AI isn’t answering.'
              : state.plan_allows === false
                ? 'Available on Growth and above only.'
                : 'This is an add-on.'}{' '}
            {isSuperadmin ? (
              'Grant it as a feature override on the tenant, or the customer buys it on their Billing page.'
            ) : (
              <Link to={`/t/${tenantId}/billing`} className="font-medium text-indigo-600 hover:underline">
                {state.plan_allows === false ? 'Upgrade on the Billing page' : 'Add it on the Billing page'}
              </Link>
            )}
          </p>
        )}

        {/* Not gated on `entitled`: when an add-on lapses the customer
            still has to be able to see the account and disconnect it. */}
        {connection && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-base font-semibold text-gray-900">
                  {connection.username ? `@${connection.username}` : connection.ig_account_id}
                </p>
                {connection.account_name && <p className="text-sm text-gray-500">{connection.account_name}</p>}
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                connection.status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {connection.status === 'connected' ? 'Connected' : 'Needs attention'}
              </span>
            </div>

            {connection.last_error && (
              <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                {connection.last_error}
              </p>
            )}

            {/* The one thing still to do at Meta, and the only place the
                customer can read these two values. */}
            {connection.webhook_url ? (
              <div className="space-y-2 rounded-md border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm font-semibold text-blue-900">One step left, in your own Meta app</p>
                <p className="text-sm text-blue-900">
                  Under Webhooks, set the Callback URL and Verify token below, then subscribe the
                  Instagram <span className="font-mono">messages</span> field.
                </p>
                {[['Callback URL', connection.webhook_url], ['Verify token', connection.webhook_verify_token]]
                  .filter(([, value]) => Boolean(value))
                  .map(([what, value]) => (
                    <div key={what}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-800">{what}</p>
                      <div className="mt-1 flex items-start gap-2">
                        <code className="flex-1 break-all rounded bg-white px-2 py-1.5 text-xs text-gray-800">{value}</code>
                        <button type="button" onClick={() => copy(value, what)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-blue-300 bg-white px-2 py-1.5 text-xs font-medium text-blue-800">
                          <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" /> Copy
                        </button>
                      </div>
                    </div>
                  ))}
                <p className="text-xs text-blue-800">
                  Treat the callback URL like a password: anyone holding it can post messages into this workspace.
                </p>
              </div>
            ) : (
              <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                This account delivers to its own callback URL, but the API has no address configured
                (API_BASE_URL), so it can&rsquo;t be shown. Ask Serviti support for it.
              </p>
            )}

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={Boolean(connection.ai_enabled)}
                disabled={busy}
                onChange={(event) => change(
                  { ai_enabled: event.target.checked },
                  event.target.checked ? 'The AI is answering Instagram again.' : 'The AI has stopped answering Instagram.',
                )}
                className="h-4 w-4 rounded border-gray-300 text-green-600"
              />
              Let the AI answer direct messages
            </label>

            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => setOpen(true)} disabled={busy}
                className="text-sm font-medium text-indigo-600 hover:underline disabled:opacity-50">
                Update credentials
              </button>
              <button type="button" onClick={disconnect} disabled={busy}
                className="text-sm font-medium text-red-700 hover:underline disabled:opacity-50">
                Disconnect
              </button>
            </div>
          </>
        )}

        {entitled && !connection && !open && (
          <>
            <p className="text-sm text-gray-700">
              Connect the Instagram professional account you already run in your own Meta app. Your app
              keeps its own webhook and stays in control of the account &mdash; we only need to be able
              to reply.
            </p>
            <button type="button" onClick={() => setOpen(true)}
              className="rounded-md bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-700">
              Connect Instagram
            </button>
          </>
        )}

        {entitled && open && (
          <form onSubmit={connect} className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm text-gray-600">
              From your Meta app dashboard, under Instagram. Do not replace a webhook another
              integration is using.
            </p>
            <div>
              <label htmlFor="ig-account" className={label}>Instagram account ID</label>
              <input id="ig-account" value={fields.ig_account_id} onChange={set('ig_account_id')}
                placeholder="17841400000000001" className={input} autoComplete="off" />
            </div>
            <div>
              <label htmlFor="ig-page" className={label}>
                Facebook Page ID <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <input id="ig-page" value={fields.page_id} onChange={set('page_id')}
                placeholder="102000000000001" className={input} autoComplete="off" />
            </div>
            <div>
              <label htmlFor="ig-token" className={label}>Access token</label>
              <input id="ig-token" type="password" value={fields.access_token} onChange={set('access_token')}
                className={input} autoComplete="off" />
              <p className="mt-1 text-xs text-gray-500">
                A Page or System User token with{' '}
                <span className="font-mono">instagram_manage_messages</span>. It is encrypted and never shown again.
              </p>
            </div>
            <div>
              <label htmlFor="ig-secret" className={label}>App Secret (required)</label>
              <input id="ig-secret" type="password" value={fields.app_secret} onChange={set('app_secret')}
                className={input} autoComplete="off" />
              <p className="mt-1 text-xs text-gray-500">
                Required to verify that incoming messages came from your Meta app. Re-enter it when
                updating credentials.
              </p>
            </div>
            <div>
              <label htmlFor="ig-callback-use" className={label}>
                Does this app callback serve another integration or account?
              </label>
              <select id="ig-callback-use" value={callbackUse} className={input}
                onChange={(event) => setCallbackUse(event.target.value)}>
                <option value="">Choose before continuing</option>
                <option value="dedicated">No — this callback is dedicated to this account and Serviti</option>
                <option value="shared">Yes, or I am not sure</option>
              </select>
              {callbackUse === 'shared' && (
                <p role="alert" className="mt-2 text-sm text-amber-900">
                  Stop here: changing that callback could disconnect your existing system. Contact
                  Serviti support to arrange shared-app routing.
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button type="submit" disabled={busy || !ready}
                className="rounded-md bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-700 disabled:opacity-50">
                {busy ? 'Checking with Meta…' : connection ? 'Save credentials' : 'Connect'}
              </button>
              <button type="button" onClick={() => setOpen(false)} disabled={busy}
                className="text-sm text-gray-600 hover:underline">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default InstagramAgentCard;
